/**
 * MONTHLY PAYOUT AT FULL USE — asked by the CMD, 31-10-2026.
 *
 * Sits under the Lines card on a hire order, in muted teal so it reads as a
 * different kind of figure: a forecast from the order's own terms, not
 * anything measured or certified. Draws NOTHING unless the order has a hire
 * line, so every other work order looks exactly as it did. The arithmetic is
 * in ../lib/hire-payout.ts.
 */
import { CalendarRange } from 'lucide-react'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import { Card } from '@/components/ui'
import { hirePayout } from '../lib/hire-payout'

const n = (v: unknown) => Number(v ?? 0) || 0
const r2 = (v: number) => Math.round(v * 100) / 100

function monthName(iso: string) {
  const [y, m] = iso.split('-').map(Number)
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })
}

export function WoHirePayout({ woId }: { woId: string }) {
  const { data: lines } = useQuery<any>(`wo_payout_lines_${woId}`,
    () => supabase.from('wo_line_items')
      .select('id, quantity, rate, amount, hire_units, gst_rate_percent')
      .eq('wo_id', woId).eq('pricing_basis', 'EQUIPMENT_HIRE'),
    [woId], { cacheable: false })
  const has = (lines as any[]).length > 0

  const { data: head } = useQuery<any>(`wo_payout_head_${woId}`,
    () => supabase.from('work_orders')
      .select('id, start_date, contract_value, gst_treatment, is_reverse_charge, gst_amount, '
              + 'tds_pct, tds_section, retention_pct, advance_pct, advance_recovery_pct')
      .eq('id', woId),
    [woId], { cacheable: false, enabled: has })
  const { data: verdicts } = useQuery<any>(`wo_payout_verdicts_${woId}`,
    () => supabase.from('v_rcm_determination').select('wo_line_id, verdict_notified').eq('wo_id', woId),
    [woId], { cacheable: false, idField: 'wo_line_id', enabled: has })

  const h = (head as any[])[0]
  if (!has || !h) return null

  const p = hirePayout(
    { start_date: h.start_date, contract_value: n(h.contract_value),
      gst_treatment: h.gst_treatment, is_reverse_charge: Boolean(h.is_reverse_charge),
      gst_amount: n(h.gst_amount), tds_pct: h.tds_pct, retention_pct: h.retention_pct,
      advance_pct: h.advance_pct, advance_recovery_pct: h.advance_recovery_pct },
    (lines as any[]).map((l) => ({
      id: l.id, quantity: n(l.quantity), rate: n(l.rate), amount: n(l.amount),
      hire_units: l.hire_units, gst_rate_percent: l.gst_rate_percent,
      verdict: (verdicts as any[]).find((v) => v.wo_line_id === l.id)?.verdict_notified,
    })))

  const title = (
    <span className="flex items-center gap-2 text-teal-900">
      <CalendarRange className="h-4 w-4" /> Monthly payout at full use
    </span>
  )

  if (!p) {
    return (
      <Card title={title} className="border-teal-200 bg-teal-50">
        <p className="text-sm text-teal-900">Give the order a start date to see its month-by-month payout.</p>
      </Card>
    )
  }

  const showRcm = p.totals.gstRcm > 0
  const showRet = p.totals.retention > 0
  const showAdv = p.advancePaid > 0
  // The month to headline: the longest one, preferring one the advance does
  // not touch - the CMD's question is the ordinary month, not the first.
  const ordinary = p.months.filter((m) => m.advance === 0)
  const pool = ordinary.length > 0 ? ordinary : p.months
  const typical = pool.reduce((a, m) => (m.days > a.days ? m : a), pool[0])

  return (
    <Card title={title} className="border-teal-200 bg-teal-50"
          subtitle={<span className="text-teal-800">
            A forecast from this order's own terms, assuming every item is on hire every
            day of each calendar month from {ddmmyyyy(h.start_date)}, with no breakdowns or
            early returns. Actual bills come from the certified log sheets.
          </span>}>
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-teal-200 bg-white px-4 py-3">
          <p className="text-xs font-medium text-slate-500">A full month ({typical.days} days), bill incl. GST</p>
          <p className="mt-0.5 text-xl font-semibold text-teal-900">{rupees(typical.bill)}</p>
        </div>
        <div className="rounded-lg border border-teal-200 bg-white px-4 py-3">
          <p className="text-xs font-medium text-slate-500">Paid to the vendor that month</p>
          <p className="mt-0.5 text-xl font-semibold text-teal-900">{rupees(typical.net)}</p>
          <p className="text-[11px] text-slate-500">after TDS{showRet ? ', retention' : ''}{typical.advance > 0 ? ' and advance adjustment' : ''}</p>
        </div>
        <div className="rounded-lg border border-teal-200 bg-white px-4 py-3">
          <p className="text-xs font-medium text-slate-500">Whole order, {p.months.length} months, incl. GST</p>
          <p className="mt-0.5 text-xl font-semibold text-teal-900">{rupees(p.totals.bill)}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-teal-200 bg-white">
        <table className="tbl">
          <thead>
            <tr>
              <th>Month</th><th className="num">Days</th><th className="num">Hire value</th>
              <th className="num">GST charged</th><th className="num">Bill total</th>
              {showRcm && <th className="num">GST by OVAL (RCM)</th>}
              <th className="num">TDS{h.tds_section ? ` (${h.tds_section})` : ''}</th>
              {showRet && <th className="num">Retention</th>}
              {showAdv && <th className="num">Advance adjusted</th>}
              <th className="num">Paid to vendor</th>
            </tr>
          </thead>
          <tbody>
            {showAdv && (
              <tr>
                <td colSpan={2} className="text-xs">Before start: mobilisation advance</td>
                <td className="num" colSpan={3 + (showRcm ? 1 : 0) + 1 + (showRet ? 1 : 0) + 1} />
                <td className="num font-semibold">{rupees(p.advancePaid)}</td>
              </tr>
            )}
            {p.months.map((m) => (
              <tr key={m.month}>
                <td className="whitespace-nowrap">{monthName(m.month)}</td>
                <td className="num">{m.days}</td>
                <td className="num">{rupees(m.value)}</td>
                <td className="num">{rupees(m.gstCharged)}</td>
                <td className="num">{rupees(m.bill)}</td>
                {showRcm && <td className="num">{rupees(m.gstRcm)}</td>}
                <td className="num">− {rupees(m.tds)}</td>
                {showRet && <td className="num">− {rupees(m.retention)}</td>}
                {showAdv && <td className="num">{m.advance > 0 ? `− ${rupees(m.advance)}` : '—'}</td>}
                <td className="num font-semibold text-teal-900">{rupees(m.net)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td>Total</td><td />
              <td className="num">{rupees(p.totals.value)}</td>
              <td className="num">{rupees(p.totals.gstCharged)}</td>
              <td className="num">{rupees(p.totals.bill)}</td>
              {showRcm && <td className="num">{rupees(p.totals.gstRcm)}</td>}
              <td className="num">− {rupees(p.totals.tds)}</td>
              {showRet && <td className="num">− {rupees(p.totals.retention)}</td>}
              {showAdv && <td className="num">− {rupees(p.totals.advance)}</td>}
              <td className="num text-teal-900">{rupees(r2(p.totals.net + p.advancePaid))}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-xs text-teal-800">
        The total paid to the vendor includes the advance. TDS is worked out on the value before GST.
        {showAdv && n(h.advance_recovery_pct) > 0
          ? ` The advance is adjusted at ${n(h.advance_recovery_pct)}% of each bill's value until it is fully recovered.`
          : ''}
        {' '}Days are counted from the start date for the days each line buys; the last month is what is left.
      </p>
    </Card>
  )
}
